import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, Sparkles, ShieldCheck, Globe2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useKarmStats } from "@/hooks/useKarmStats";

export default function PricingPage() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const stats = useKarmStats();

  const subscribe = async () => {
    if (!user) { toast.error("Please sign in first"); return; }
    setLoading(true);
    try {
      await api.post("/billing/subscribe");
      await refresh();
      toast.success("Welcome to KARM Plus! (Mock payment for MVP)");
    } catch {
      toast.error("Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-16">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="eyebrow mb-3">Pricing</p>
        <h1 className="display-1 mb-5">One plan. Unfair advantage.</h1>
        <p className="text-lg text-muted-foreground">No tiers, no upsells. Everything we build, for everyone serious about a global GCC career.</p>
      </div>

      <div className="vital-card-static p-8 md:p-12 max-w-2xl mx-auto" data-testid="pricing-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="eyebrow mb-1">KARM Plus</p>
            <h2 className="display-2 text-3xl">$5<span className="text-lg font-normal text-muted-foreground">/month</span></h2>
          </div>
          <div className="h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
            <Crown className="h-6 w-6" />
          </div>
        </div>

        <ul className="space-y-3 mb-8">
          {[
            [`Unlimited access to the ${stats.companies.toLocaleString()}+ GCC company directory`, Globe2],
            [`Daily-refreshed feed of ${stats.jobs.toLocaleString()}+ live roles`, Building2],
            ["AI Content Studio — text, images, multi-platform publishing", Sparkles],
            ["Custom profile labels & quick resume upload", ShieldCheck],
            ["Priority job recommendations tailored to your skills", Check],
            ["Cancel anytime. No questions asked.", Check],
          ].map(([t, Icon]) => (
            <li key={t} className="flex items-start gap-3">
              <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="h-3.5 w-3.5" />
              </div>
              <span className="text-sm">{t}</span>
            </li>
          ))}
        </ul>

        {user?.is_subscribed ? (
          <div className="rounded-lg bg-primary/10 border border-primary/30 p-4 text-center">
            <Crown className="h-5 w-5 text-primary inline mr-2" />
            <span className="font-semibold">You're on KARM Plus</span>
            <div className="text-xs text-muted-foreground mt-1">Active until {new Date(user.subscription_until).toLocaleDateString()}</div>
          </div>
        ) : (
          <Button onClick={subscribe} disabled={loading} size="lg" className="w-full h-12" data-testid="subscribe-btn">
            {loading ? "Processing…" : "Subscribe with Razorpay"}
          </Button>
        )}
        <p className="text-xs text-center text-muted-foreground mt-4">
          MVP: Razorpay flow is mocked. Real card-on-file integration ships next.
        </p>
      </div>
    </div>
  );
}
